import type { TimeSlot } from "./enums";

/**
 * 便区分 (早朝/午前/午後) を判定する時刻境界。"HH:MM" 形式 (24h, ゼロ埋め)。
 * システム設定 (system_settings) で保持し、注文画面・配車画面が共通参照する。
 */
export type TimeSlotBoundaries = {
  /** この時刻未満を早朝便とする (例: "07:00")。 */
  earlyMorningUntil: string;
  /** この時刻未満を午前便、以降を午後便とする (例: "14:00")。 */
  amUntil: string;
};

/**
 * 境界の**初期値**。要件上の便区分 (早朝 00:00-07:00 / 午前 07:00-14:00 / 午後 14:00-24:00) に揃える。
 * #640: 午後枠の終端は当日終端 (24:00) まで伸ばし、3 枠で 1 日を隙間なく覆う。
 * #641: 午前/午後の境界は納品時刻 14:00 (運用確認済み)。13 時台の納品は午前便に含める。
 *
 * **運用値の単一源泉は `system_settings` (DB) であってこの定数ではない。**
 * 用途は「まだ DB の値が無い/読めない」3 つだけ:
 * カラム DEFAULT (新規環境の初期行) / 設定フォームの読込前初期値 / テストの既定境界。
 *
 * この定数を分類関数のデフォルト引数にしてはいけない (#641)。省略できると、設定を読み忘れた
 * 画面が黙って初期値で判定し、同じ注文が画面によって午前/午後で食い違う。
 * `classifyTimeSlot` 等は境界を必須引数にしてあるので、渡し忘れは型エラーで露見する。
 */
export const DEFAULT_TIME_SLOT_BOUNDARIES: TimeSlotBoundaries = {
  earlyMorningUntil: "07:00",
  amUntil: "14:00",
};

/** 早朝便の固定開始。開始時刻としての "00:00" は当日 0 時を指す。 */
export const TIME_SLOT_DAY_START = "00:00";

/**
 * 午後便の終端の既定 (#640)。**"00:00" は「当日の終端 = 24:00」を意味する**。
 *
 * 便区分は早朝・午前・午後の 3 枠だけで 1 日を隙間なく覆う方針のため、午後枠は当日の
 * 終わりまで伸ばす (19:00〜24:00 が窓から外れて 20:30 納品が分類できない状態を解消する)。
 * "HH:MM" では 24:00 を表現できないので、**終端として使う箇所は必ず
 * `resolveDayEndMinutes` / `resolveDayEndSeconds` / `resolveDayEndHour` を通す**こと
 * (開始時刻としての "00:00" = 0 と混同すると窓が逆転する)。
 */
export const TIME_SLOT_PM_END = "00:00";

/**
 * 配車 (ルート最適化) の稼働終端の既定 (#640)。
 * 便区分 (請求) の終端が 24:00 まで伸びても、デポの稼働終端はこれとは独立に持つ。
 * 同一視すると OR-Tools が深夜まで配達を組める解を許容してしまう。
 */
export const TIME_SLOT_DISPATCH_END = "19:00";

/** 当日終端 (24:00) を分/秒/時で表した値。終端としての "00:00" の解決結果。 */
export const TIME_SLOT_DAY_END_MINUTES = 24 * 60;
export const TIME_SLOT_DAY_END_SECONDS = 24 * 3600;
export const TIME_SLOT_DAY_END_HOUR = 24;

/**
 * "HH:MM" (24 時間表記・ゼロ埋め) の**正準な集合**。時は 00-23、分は 00-59 だけを受ける。
 *
 * 時刻の形式を検査する箇所はここを参照する。受注入力の境界 (`packages/contract` の
 * `TimeOfDaySchema`) も同じパターンを使う (#751)。自前の正規表現を各所に置くと集合が
 * ずれる — 実際 #733 では区切り文字の集合が 2 箇所にあってパーサごとに通る値が違った。
 */
export const HHMM_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * "HH:MM" を当日 0 時起点の分に変換する (**開始時刻としての解釈**)。
 * "00:00" は 0 分。空・不正形式は null を返し、未入力を 00:00 に丸めない (#640)。
 */
export function parseTimeOfDayMinutes(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = HHMM_PATTERN.exec(value);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

/**
 * **終端時刻**としての "HH:MM" を当日 0 時起点の分に解決する (#640)。
 * "00:00" だけは当日の終端 (24:00 = 1440 分) として扱う。
 *
 * 開始時刻としての "00:00" (= 0 分) とは意味が衝突するため、終端に使う箇所
 * (便区分窓の右端・稼働終端・時間軸の右端) からのみ呼ぶこと。
 */
export function resolveDayEndMinutes(value: string | null | undefined): number | null {
  const minutes = parseTimeOfDayMinutes(value);
  if (minutes === null) return null;
  return minutes === 0 ? TIME_SLOT_DAY_END_MINUTES : minutes;
}

/** 終端時刻 "HH:MM" を当日 0 時起点の秒に解決する ("00:00" = 86400)。 */
export function resolveDayEndSeconds(value: string | null | undefined): number | null {
  const minutes = resolveDayEndMinutes(value);
  return minutes === null ? null : minutes * 60;
}

/** 終端時刻 "HH:MM" を時 (小数可) に解決する ("00:00" = 24)。 */
export function resolveDayEndHour(value: string | null | undefined): number | null {
  const minutes = resolveDayEndMinutes(value);
  return minutes === null ? null : minutes / 60;
}

/**
 * 配達開始時刻 ("HH:MM") を便区分に分類する。
 * - `earlyMorningUntil` 未満 → early_morning
 * - `amUntil` 未満 → am
 * - それ以外 → pm
 * 時刻が空 (null/undefined/"") の場合は判定不能として null を返す。
 */
export function classifyTimeSlot(
  time: string | null | undefined,
  boundaries: TimeSlotBoundaries,
): TimeSlot | null {
  if (!time) return null;
  if (time < boundaries.earlyMorningUntil) return "early_morning";
  if (time < boundaries.amUntil) return "am";
  return "pm";
}

/**
 * 便区分の判定に使う配達指定時刻 (#659)。範囲指定なら `end` が終了時刻、単一時刻なら `end` は空。
 *
 * **両方のプロパティを必須にしてある**のは、終了時刻を渡し忘れた呼び出しを型エラーで露見させるため。
 * 省略できると、その画面だけ開始時刻で判定して同じ注文が午前/午後で食い違う (#641 と同じ事故)。
 */
export type DeliveryTimeRange = {
  /** 配達指定の開始時刻 "HH:MM"。未指定は null/undefined/""。 */
  start: string | null | undefined;
  /** 配達指定の終了時刻 "HH:MM"。単一時刻指定のときは未指定。 */
  end: string | null | undefined;
};

/**
 * 配達指定時刻を便区分に分類する (#659)。**範囲指定は終了時刻で判定する**。
 *
 * `13:30〜14:30` は午後便。運用確認済み (2026-08-12): 14:00 を跨ぐ配達は午後便扱いとし、
 * 午前便にしたい案件は**終了時刻を 14:00 より前に指定する**ことで受注入力側から制御する。
 *
 * - 終了時刻があればそれで判定する (読めない値なら開始時刻へ落とす)
 * - 終了時刻が無い (単一時刻指定) 場合は開始時刻で判定する
 * - どちらも無い/読めない場合は判定不能として null (呼び出し側が既定便へ丸める)
 */
export function classifyDeliveryTimeSlot(
  deliveryTime: DeliveryTimeRange,
  boundaries: TimeSlotBoundaries,
): TimeSlot | null {
  const basis = pickTimeSlotBasis(deliveryTime);
  if (basis.fromEnd && basis.minutes === TIME_SLOT_DAY_END_MINUTES) return "pm";
  return classifyTimeSlot(basis.time, boundaries);
}

/** 便区分の判定に使う時刻の選択結果。`fromEnd` は終了時刻を採ったか (終端 "00:00" の解釈が変わる)。 */
type TimeSlotBasis = {
  /** 正規化済み "HH:MM"。判定不能なら null */
  time: string | null;
  /** 当日 0 時起点の分。終端としての "00:00" は当日終端 (1440)。判定不能なら null */
  minutes: number | null;
  fromEnd: boolean;
};

/**
 * 便区分の判定に「終了時刻と開始時刻のどちらを使うか」を決める (#659)。
 *
 * 分類 (`classifyDeliveryTimeSlot`) と、境界ちょうどの明細かの照合 (`resolveTimeSlotBasisMinutes`)
 * が別々に選択規則を持つと、同じ明細で見ている時刻が食い違う。選択規則はここ 1 箇所に閉じる。
 */
function pickTimeSlotBasis(deliveryTime: DeliveryTimeRange): TimeSlotBasis {
  // 表記ゆれ ("9:30" / "07:30:00" / 全角) を先に "HH:MM" へ揃える。`classifyTimeSlot` は
  // 文字列比較なので、ゼロ埋めなしを渡すと "9:30" > "14:00" と評価されて午後便になり、
  // 読めない値 ("指定なし" 等) も黙って午後便になる。便区分は請求単位のキーなので、
  // 正規化できない値は判定材料にしない。
  const start = normalizeTimeOfDay(deliveryTime.start);
  const end = normalizeTimeOfDay(deliveryTime.end);
  const startMinutes = parseTimeOfDayMinutes(start);
  // 終端としての "00:00" は当日終端 (24:00) に解決される。開始時刻の 0 時と混同すると
  // 22:00〜00:00 の配達が早朝便になる (#640)。
  const endMinutes = resolveDayEndMinutes(end);
  // 終了が開始より前の範囲は 0 時をまたぐ不正データ (contract の `refineSameDayWindow` が
  // 弾く形。seed / 取込 / 直接更新で入りうる)。時間窓側 (`resolveWindowWithFit`) は終了を
  // 開始まで引き上げるので、分類だけ終了を採ると便区分と時間窓が食い違い、注文が黙って
  // 便窓の外へ出て未割当になる。範囲として壊れている場合は開始時刻で判定する。
  if (endMinutes !== null && (startMinutes === null || startMinutes <= endMinutes)) {
    return { time: end, minutes: endMinutes, fromEnd: true };
  }
  return { time: start, minutes: startMinutes, fromEnd: false };
}

/**
 * 便区分の判定に**実際に使った時刻**を当日 0 時起点の分で返す (#709)。判定不能なら null。
 *
 * 用途は「その明細が便区分の境界ちょうどか」の照合。業務日報の便 (`work-report-trip.ts`) は
 * 境界ちょうどの明細だけで成る便を 1 つ前の便区分の便へ吸収するため、境界値と突き合わせる
 * 時刻が要る。**分類と同じ時刻を見る**ために `classifyDeliveryTimeSlot` と選択規則を共有する。
 *
 * 終端としての "00:00" は当日終端 (24:00 = 1440 分) を返す。境界 ("HH:MM") は 1440 分に
 * ならないので、22:00〜00:00 の配達が境界ちょうどと誤判定されることはない。
 */
export function resolveTimeSlotBasisMinutes(deliveryTime: DeliveryTimeRange): number | null {
  return pickTimeSlotBasis(deliveryTime).minutes;
}

/**
 * 受注を配送計画の便区分へ分類する。範囲指定は終了時刻で判定する (#659)。
 * 配達時刻の指定が無い受注は通常運用の午前便とし、早朝便には明示時刻がある受注だけを含める。
 */
export function classifyOrderTimeSlot(
  deliveryTime: DeliveryTimeRange,
  boundaries: TimeSlotBoundaries,
): TimeSlot {
  return classifyDeliveryTimeSlot(deliveryTime, boundaries) ?? "am";
}

/** 全角数字を半角へ寄せるためのコードポイント差 (`０` - `0`)。 */
const FULLWIDTH_DIGIT_OFFSET = 0xfee0;

/** 全角数字・全角コロンを半角へ寄せる (表記ゆれの入口で 1 度だけ通す)。 */
function toHalfWidthTimeText(text: string): string {
  return text
    .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - FULLWIDTH_DIGIT_OFFSET))
    .replace(/：/g, ":");
}

/** 時刻 1 つ分 (時:分[:秒])。分・秒は 0-59 のみ受け、時は 23 以下を `hhmmOf` で検査する。 */
const TIME_SOURCE = String.raw`(\d{1,2}):([0-5]\d)(?::[0-5]\d)?`;

/**
 * 範囲の区切りの**正準集合**。現行 Excel 由来の `〜` `～` `~` `-` に加えてダッシュ類と「から」を受ける。
 *
 * **注文書パーサはここへ委譲し、区切りを手書きしない** (`packages/contract` の DTO と同じで、
 * 集合が 2 箇所にあると必ずずれる)。実際 #733 / #749 / #774 で**パーサ 5 本**が同じ穴を通した。
 * 新しい区切りが実データで出たら**共有側に足す** — パーサ側で上乗せしない。
 *
 * **半角と全角は対で持つ。** `~` と `～` があるのに `-` の全角 `－` が無い、のような欠けは、
 * NFKC 正規化を通すパーサだけ通って通さないパーサ (cqree) が落ちる、という様式ごとの
 * 食い違いになる (#774)。
 *
 * 未収録: `‐` (U+2010) / `ー` (U+30FC)。実データで出たらここへ足す。
 * `ー` は長音記号でもあるため、足すときは日本語の語中に当たらないか確かめること。
 *
 * **区切りを必須にしてある**のは、`"1:30 (前日 23:00 積込)"` のような注記付きの値から
 * 2 つ目の時刻を終了時刻と誤認しないため (便区分が変わると請求額が変わる)。
 */
const RANGE_SEPARATOR_SOURCE = String.raw`\s*(?:から|[-－–—−~〜～])\s*`;

/** 単一時刻は先頭一致で読む (文中から拾わない)。 */
const SINGLE_TIME_PATTERN = new RegExp(`^${TIME_SOURCE}`);
/** 範囲は文中から拾う ("配達 16:30〜17:00" のような前置きを許す)。 */
const TIME_RANGE_PATTERN = new RegExp(`${TIME_SOURCE}${RANGE_SEPARATOR_SOURCE}${TIME_SOURCE}`);

/** 正規表現の (時, 分) キャプチャを "HH:MM" にする。24 時以降の表記は読めない値として null。 */
function hhmmOf(hour: string | undefined, minute: string | undefined): string | null {
  if (hour === undefined || minute === undefined) return null;
  const h = Number(hour);
  if (h > 23) return null;
  return `${String(h).padStart(2, "0")}:${minute}`;
}

/**
 * 単一時刻の表記を "HH:MM" に正規化する。
 * 現行 Excel からの取込値は `"7:30"` `"07:30:00"` `"１１：３０"` のような表記も混ざるため、
 * 境界 (分まで意味を持つ) と比較する前にゼロ埋め・秒切り捨て・半角化を行う。
 * 時刻として読めない値は null (未入力と同じ「判定不能」扱い) にして 00:00 に丸めない。
 */
export function normalizeTimeOfDay(value: string | null | undefined): string | null {
  if (!value) return null;
  const match = SINGLE_TIME_PATTERN.exec(toHalfWidthTimeText(value).trim());
  return match ? hhmmOf(match[1], match[2]) : null;
}

/**
 * `"16:30〜17:00"` のような 1 カラムの時刻表記から開始・終了を "HH:MM" で取り出す (#659)。
 *
 * 業務日報 (`work_report_entries.delivery_time` / `pickup_time`) は範囲を 1 カラムの文字列で持ち、
 * 現行 Excel 由来の取込値は区切り文字 (`〜` `～` `~` `-`)・全角コロン (`11：30`)・
 * ゼロ埋めなし (`7:30`)・秒付き (`07:30:00`) が混在する。便区分は請求単位のキーなので、
 * 表記ゆれの解釈を 1 箇所に閉じて配送側 (`order_locations.time_start/time_end`) と同じ判定に通す。
 *
 * 範囲と認めるのは**区切り文字で結ばれた 2 つの時刻**だけ。3 つ以上並ぶ場合は最初の範囲を採り、
 * 区切りが無ければ単一時刻指定として `end` は null にする。24 時以降の表記 (`25:00`) は読めない
 * 値として扱い、片側だけ読めた場合は単一時刻へ落とす。1 つも読めなければ null を返し、
 * 未入力を 00:00 に丸めない。
 */
export function parseTimeRangeText(text: string | null | undefined): DeliveryTimeRange | null {
  if (!text) return null;
  const normalized = toHalfWidthTimeText(text).trim();
  const range = TIME_RANGE_PATTERN.exec(normalized);
  if (range) {
    const start = hhmmOf(range[1], range[2]);
    const end = hhmmOf(range[3], range[4]);
    if (start !== null && end !== null) return { start, end };
    const single = start ?? end;
    return single === null ? null : { start: single, end: null };
  }
  const single = normalizeTimeOfDay(normalized);
  return single === null ? null : { start: single, end: null };
}

/**
 * ISO8601 (offset 付き) の時刻を JST の "HH:MM" に変換して便区分に分類する (#425)。
 * レーン (route) が日単位になり便の所属を持たないため、stop 単位の便は
 * 到着予定/実績時刻から導出する。時刻が無い・不正な場合は null。
 */
export function classifyIsoTimeSlot(
  iso: string | null | undefined,
  boundaries: TimeSlotBoundaries,
): TimeSlot | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  const jst = new Date(ms + 9 * 3600_000);
  const hm = `${String(jst.getUTCHours()).padStart(2, "0")}:${String(jst.getUTCMinutes()).padStart(2, "0")}`;
  return classifyTimeSlot(hm, boundaries);
}

/** deriveRouteTimeSlots が参照する stop の最小形。実績があれば実績、無ければ予定時刻で判定する。 */
export interface StopTimeForSlot {
  estimated_arrival?: string | null;
  actual_arrival?: string | null;
}

/**
 * 日単位レーン (route) の stops から、そのレーンが跨る便の一覧を出現順 (重複なし) で導出する (#425)。
 * 日報の担当便・配送状況ボード・距離実績など、表示/帳票用の便ラベルの供給元。
 * 時刻を持つ stop が 1 件も無ければ空配列。
 */
export function deriveRouteTimeSlots(
  stops: readonly StopTimeForSlot[],
  boundaries: TimeSlotBoundaries,
): TimeSlot[] {
  const seen: TimeSlot[] = [];
  for (const stop of stops) {
    const slot = classifyIsoTimeSlot(stop.actual_arrival ?? stop.estimated_arrival, boundaries);
    if (slot && !seen.includes(slot)) seen.push(slot);
  }
  return seen;
}

/** レーンの代表便 (最初に到達する便)。導出不能時は null (#425)。 */
export function deriveRepresentativeTimeSlot(
  stops: readonly StopTimeForSlot[],
  boundaries: TimeSlotBoundaries,
): TimeSlot | null {
  return deriveRouteTimeSlots(stops, boundaries)[0] ?? null;
}

/**
 * 注文書取込の時刻補完に使う許容幅の既定値 (分, #657)。
 *
 * DB カラムの DEFAULT / seed の投入値 / 管理画面のフォーム初期値の**単一源泉**。
 * database の `DEFAULT_IMPORT_TIME_TOLERANCE` と admin の設定画面はどちらもここを参照する
 * (既定値を変えたときに片方だけ古い値が残るのを防ぐ。#353 と同じ方針)。
 *
 * **0 は「補完しない」** を意味する。開始と終了が同一時刻の枠は optimizer が 1 点の窓として
 * 解くため実質配車不能になるので、幅ゼロの補完は行わない。
 */
export const IMPORT_TIME_TOLERANCE_DEFAULT_MINUTES = {
  earlyArrival: 15,
  lateArrival: 15,
} as const;
